import { useState, useEffect, useRef } from "react";
import { inventoryItemDtlApi, productApi, inventorySummaryApi } from "../api/api";
import "./common.css";
import { useResizableColumns } from "../hooks/useResizableColumns";
import { usePagination }       from "../hooks/usePagination";
import Pagination              from "../components/Pagination";
import BarcodeLabelPrinter from "./BarcodeLabelPrinter";

const BASE_URL = "https://api-dev.revajewellers.ashtamtechnologies.com/api";

/* ══════════════════════════════════════
   PRINT LABEL MODAL — Horizontal Tag
   86 × 40 mm landscape hang tag
   Left (blue): RJ · barcode · Sz/Purity
   Right (white): VendorName · ProductCode · G/L/N · #id
══════════════════════════════════════ */
function PrintLabelModal({ item, productName, onClose }) {
  const [copies,    setCopies]    = useState(1);
  const [shopCode,  setShopCode]  = useState("RJ");
  const [generating, setGenerating] = useState(false);

  /* Enriched data fetched from Product + Vendor */
  const [enriched, setEnriched]   = useState(null);
  const [fetching, setFetching]   = useState(true);

  const previewRef = useRef(null);

  const fmt = (v) => (parseFloat(v) || 0).toFixed(3);

  /* ── Fetch product + vendor for this item ── */
  useEffect(() => {
    const load = async () => {
      setFetching(true);
      try {
        const token = localStorage.getItem("authToken") || "";
        const headers = {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        };
        const norm = (r) => Array.isArray(r) ? r[0] : (r?.data ?? r);

        let vendorName     = null;
        let productCode    = null;
        let purityStandard = null;

        if (item.productID) {
          const pRes     = await fetch(`${BASE_URL}/Product/${item.productID}`, { headers });
          const pData    = await pRes.json();
          const product  = norm(pData);
          productCode    = product?.productCode    ?? null;
          purityStandard = product?.purityStandard ?? null;

          if (product?.vendorID) {
            const vRes   = await fetch(`${BASE_URL}/Vendor/${product.vendorID}`, { headers });
            const vData  = await vRes.json();
            const vendor = norm(vData);
            vendorName   = vendor?.vendorName ?? null;
          }
        }

        setEnriched({ vendorName, productCode, purityStandard });
      } catch (_) {
        setEnriched({ vendorName: null, productCode: null, purityStandard: null });
      } finally {
        setFetching(false);
      }
    };
    load();
  }, [item.productID]);

  /* ── Barcode: encodes the inventoryItemID ── */
  const barcodeValue = String(item.inventoryItemID);

  /* ── SVG barcode maker ── */
  function makeSVGBarcode(value, widthPx, heightPx, bgColor = "#cce8f0") {
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    try {
      window.JsBarcode(svg, value, {
        format: "CODE128", width: 1.1, height: heightPx,
        displayValue: false, margin: 0,
        background: bgColor, lineColor: "#000",
      });
    } catch (_) {}
    svg.setAttribute("width",  String(widthPx));
    svg.setAttribute("height", String(heightPx));
    return svg;
  }

  /* ── Build horizontal tag DOM ──
     ┌──────────────────────┬────────────┐
     │  RJ                  │  VENDOR    │  ← large bold
     │  ████ BARCODE ████   │  ProdCode  │
     │  barcode number      │  ────────  │
     │  Sz: —  Purity: 925  │  G/L/N     │
     │                      │  #1002     │
     └──────────────────────┴────────────┘
     CSS px: 320 × 149 px  =  86 × 40 mm
  ── */
  function buildTagDOM(d, shop, bcVal, extra) {
    const wrap = document.createElement("div");
    wrap.style.cssText = `
      width:320px; height:149px; display:flex;
      border-radius:10px; overflow:hidden;
      box-shadow:0 3px 14px rgba(0,0,0,.15);
      font-family:'Courier New',monospace;
      flex-shrink:0;
      border:1px solid #d8d0c8;
    `;

    /* ── LEFT — sky blue ── */
    const left = document.createElement("div");
    left.style.cssText = `
      width:196px; flex-shrink:0;
      background:#b8dde8;
      padding:10px 12px 8px 16px;
      display:flex; flex-direction:column;
      justify-content:space-between;
      position:relative;
      border-right:1.5px dashed rgba(0,0,0,.18);
    `;

    /* Punch hole */
    const hole = document.createElement("div");
    hole.style.cssText = `
      position:absolute; top:50%; left:-8px;
      transform:translateY(-50%);
      width:16px; height:16px; border-radius:50%;
      background:#f0ece6; border:1.5px solid #b0b0b0; z-index:2;
    `;
    left.appendChild(hole);

    /* Shop code */
    const shopEl = document.createElement("div");
    shopEl.style.cssText = `
      font-size:8px; font-weight:800; letter-spacing:4px;
      color:#1a4a5a; font-family:sans-serif; text-transform:uppercase;
    `;
    shopEl.textContent = shop;
    left.appendChild(shopEl);

    /* Barcode */
    const bcWrap = document.createElement("div");
    bcWrap.style.cssText = "display:flex;justify-content:center;margin:2px 0;";
    bcWrap.appendChild(makeSVGBarcode(bcVal, 168, 44));
    left.appendChild(bcWrap);

    /* Barcode number */
    const bcNum = document.createElement("div");
    bcNum.style.cssText = `
      font-size:7px; text-align:center; color:#1a4a5a;
      letter-spacing:1.5px; font-family:'Courier New',monospace;
    `;
    bcNum.textContent = String(d.barcodeID || d.inventoryItemID);
    left.appendChild(bcNum);

    /* Sz + Purity */
    const meta = document.createElement("div");
    meta.style.cssText = `
      display:flex; gap:10px; font-size:7.5px;
      color:#1a4a5a; font-family:sans-serif; font-weight:700; margin-top:3px;
    `;
    const sz = document.createElement("span");
    sz.textContent = `Sz: ${d.quantity > 1 ? d.quantity : "—"}`;
    meta.appendChild(sz);
    if (extra?.purityStandard) {
      const pur = document.createElement("span");
      pur.textContent = `Purity: ${extra.purityStandard}`;
      meta.appendChild(pur);
    }
    left.appendChild(meta);
    wrap.appendChild(left);

    /* ── RIGHT — white ── */
    const right = document.createElement("div");
    right.style.cssText = `
      flex:1; background:#fff;
      padding:10px 12px 10px 14px;
      display:flex; flex-direction:column;
      justify-content:space-between;
    `;

    /* Vendor name — large bold */
    const vendorEl = document.createElement("div");
    vendorEl.style.cssText = `
      font-size:24px; font-weight:900; letter-spacing:2px;
      color:#111; font-family:sans-serif; line-height:1; text-transform:uppercase;
    `;
    vendorEl.textContent = extra?.vendorName || "—";
    right.appendChild(vendorEl);

    /* Product code */
    if (extra?.productCode) {
      const pcEl = document.createElement("div");
      pcEl.style.cssText = `
        font-size:8.5px; color:#555; font-family:sans-serif;
        font-weight:700; letter-spacing:1px; margin-top:1px;
      `;
      pcEl.textContent = extra.productCode;
      right.appendChild(pcEl);
    }

    /* Divider */
    const hr = document.createElement("div");
    hr.style.cssText = "width:100%;height:0.5px;background:#e0d8d0;margin:5px 0 3px;";
    right.appendChild(hr);

    /* G / L / N */
    const wt = document.createElement("div");
    wt.style.cssText = "font-size:8px;color:#333;font-family:'Courier New',monospace;line-height:1.8;";
    wt.innerHTML = `G: ${fmt(d.grossWeight)} g<br>L: ${fmt(d.lessWeight)} g<br>N: <strong>${fmt(d.netWeight)} g</strong>`;
    right.appendChild(wt);

    /* Item # */
    const itemNum = document.createElement("div");
    itemNum.style.cssText = `
      font-size:10px; font-weight:700; color:#333;
      font-family:sans-serif; letter-spacing:1px; margin-top:4px;
    `;
    itemNum.textContent = `#${d.inventoryItemID}`;
    right.appendChild(itemNum);

    wrap.appendChild(right);
    return wrap;
  }

  /* ── Render preview ── */
  useEffect(() => {
    if (!previewRef.current || fetching) return;
    previewRef.current.innerHTML = "";
    const show = Math.min(copies, 3);
    for (let i = 0; i < show; i++) {
      previewRef.current.appendChild(buildTagDOM(item, shopCode, barcodeValue, enriched));
    }
    if (copies > 3) {
      const note = document.createElement("p");
      note.style.cssText = "width:100%;text-align:center;font-size:11px;color:#b0a898;margin-top:6px;";
      note.textContent = `+${copies - 3} more copies`;
      previewRef.current.appendChild(note);
    }
  }, [item, copies, shopCode, enriched, fetching]);

  /* ── SVG → PNG (for jsPDF) ── */
  function getBarcodeDataURL() {
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    try {
      window.JsBarcode(svg, barcodeValue, {
        format: "CODE128", width: 1.1, height: 30,
        displayValue: false, margin: 0,
        background: "#b8dde8", lineColor: "#000",
      });
    } catch (_) {}
    const s = new XMLSerializer().serializeToString(svg);
    return "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(s)));
  }

  async function svgToPNG(svgDataUrl, w, h) {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const c = document.createElement("canvas");
        c.width = w; c.height = h;
        const ctx = c.getContext("2d");
        ctx.fillStyle = "#b8dde8";
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        resolve(c.toDataURL("image/png"));
      };
      img.src = svgDataUrl;
    });
  }

  /* ── Download PDF — 86 × 40 mm landscape ── */
  const downloadPDF = async () => {
    setGenerating(true);
    try {
      const { jsPDF } = window.jspdf;
      const d   = item;
      const W   = 86, H = 40, LW = 52, RX = LW + 3;
      const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: [H, W] });
      const bcPNG = await svgToPNG(getBarcodeDataURL(), 500, 100);

      for (let i = 0; i < copies; i++) {
        if (i > 0) pdf.addPage([H, W]);

        /* White bg */
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, 0, W, H, "F");

        /* Blue left panel */
        pdf.setFillColor(184, 221, 232);
        pdf.rect(0, 0, LW, H, "F");

        /* Outer border */
        pdf.setDrawColor(190, 182, 172);
        pdf.setLineWidth(0.3);
        pdf.roundedRect(0.4, 0.4, W - 0.8, H - 0.8, 2, 2, "S");

        /* Dashed divider */
        pdf.setDrawColor(140, 140, 140);
        pdf.setLineDashPattern([0.8, 0.8], 0);
        pdf.setLineWidth(0.2);
        pdf.line(LW, 0.5, LW, H - 0.5);
        pdf.setLineDashPattern([], 0);

        /* Punch hole */
        pdf.setFillColor(240, 236, 230);
        pdf.setDrawColor(160, 155, 150);
        pdf.circle(2.2, H / 2, 2, "FD");

        /* Shop code */
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(6);
        pdf.setTextColor(26, 74, 90);
        pdf.text(shopCode.toUpperCase(), 6, 5.5);

        /* Barcode image */
        pdf.addImage(bcPNG, "PNG", 5, 7.5, LW - 8, 14);

        /* Barcode number */
        pdf.setFont("courier", "normal");
        pdf.setFontSize(5.5);
        pdf.setTextColor(26, 74, 90);
        pdf.text(String(d.barcodeID || d.inventoryItemID), LW / 2, 24, { align: "center" });

        /* Sz + Purity */
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(6);
        pdf.setTextColor(26, 74, 90);
        let metaStr = `Sz: ${d.quantity > 1 ? d.quantity : "\u2014"}`;
        if (enriched?.purityStandard) metaStr += `   Purity: ${enriched.purityStandard}`;
        pdf.text(metaStr, 6, 29);

        /* Vendor — large */
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(15);
        pdf.setTextColor(17, 17, 17);
        pdf.text((enriched?.vendorName || "\u2014").toUpperCase(), RX, 9);

        /* Product code */
        if (enriched?.productCode) {
          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(6.5);
          pdf.setTextColor(85, 85, 85);
          pdf.text(enriched.productCode, RX, 13.5);
        }

        /* Divider */
        pdf.setDrawColor(210, 200, 190);
        pdf.setLineWidth(0.2);
        pdf.line(RX, 15.5, W - 2, 15.5);

        /* Weights */
        pdf.setFont("courier", "bold");
        pdf.setFontSize(6.5);
        pdf.setTextColor(51, 51, 51);
        pdf.text(`G: ${fmt(d.grossWeight)} g`, RX, 21);
        pdf.text(`L: ${fmt(d.lessWeight)} g`,  RX, 26);
        pdf.text(`N: ${fmt(d.netWeight)} g`,   RX, 31);

        /* Item # */
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(7.5);
        pdf.setTextColor(34, 34, 34);
        pdf.text(`#${d.inventoryItemID}`, RX, 37.5);
      }

      pdf.save(`rj-tag-item${d.inventoryItemID}-x${copies}.pdf`);
    } finally {
      setGenerating(false);
    }
  };

  /* ── Print ── */
  const printLabels = () => {
    const d = item;
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    try {
      window.JsBarcode(svg, barcodeValue, {
        format: "CODE128", width: 1.1, height: 30,
        displayValue: false, margin: 0,
        background: "#b8dde8", lineColor: "#000",
      });
    } catch (_) {}
    const svgB64 =
      "data:image/svg+xml;base64," +
      btoa(unescape(encodeURIComponent(new XMLSerializer().serializeToString(svg))));

    const gw = fmt(d.grossWeight), lw = fmt(d.lessWeight), nw = fmt(d.netWeight);
    const bc = d.barcodeID || d.inventoryItemID;
    let html = "";

    for (let i = 0; i < copies; i++) {
      html += `
        <div class="tag">
          <div class="tag-left">
            <div class="hole"></div>
            <div class="shop">${shopCode.toUpperCase()}</div>
            <img class="bc-img" src="${svgB64}" />
            <div class="bc-num">${bc}</div>
            <div class="meta">
              <span>Sz: ${d.quantity > 1 ? d.quantity : "—"}</span>
              ${enriched?.purityStandard ? `<span>Purity: ${enriched.purityStandard}</span>` : ""}
            </div>
          </div>
          <div class="tag-right">
            <div class="vendor">${(enriched?.vendorName || "—").toUpperCase()}</div>
            ${enriched?.productCode ? `<div class="pcode">${enriched.productCode}</div>` : ""}
            <div class="hr"></div>
            <div class="wt">G: ${gw} g<br>L: ${lw} g<br>N: <b>${nw} g</b></div>
            <div class="inum">#${d.inventoryItemID}</div>
          </div>
        </div>`;
    }

    const w = window.open("", "_blank");
    w.document.write(`<!DOCTYPE html><html><head><title>Print — Item #${d.inventoryItemID}</title>
    <style>
      *{box-sizing:border-box;margin:0;padding:0}
      body{background:#e8e4de;display:flex;flex-wrap:wrap;gap:10mm;padding:10mm;}
      .tag{width:86mm;height:40mm;display:flex;border-radius:3mm;overflow:hidden;
           box-shadow:1px 1px 5px rgba(0,0,0,.2);page-break-inside:avoid;
           border:0.3pt solid #c8c0b8;}
      .tag-left{width:52mm;background:#b8dde8;padding:2.5mm 3mm 2.5mm 4.5mm;
                display:flex;flex-direction:column;justify-content:space-between;
                position:relative;border-right:0.5pt dashed rgba(0,0,0,.2);}
      .hole{position:absolute;top:50%;left:-3mm;transform:translateY(-50%);
            width:6mm;height:6mm;border-radius:50%;background:#f0ece6;border:0.3pt solid #999;}
      .shop{font-size:5.5pt;font-weight:800;letter-spacing:3pt;color:#1a4a5a;font-family:sans-serif;}
      .bc-img{display:block;width:44mm;height:13mm;object-fit:fill;}
      .bc-num{font-size:5.5pt;text-align:center;color:#1a4a5a;
              font-family:'Courier New',monospace;letter-spacing:1pt;}
      .meta{display:flex;gap:5pt;font-size:6pt;color:#1a4a5a;font-family:sans-serif;font-weight:700;}
      .tag-right{flex:1;background:#fff;padding:2.5mm 3mm;
                 display:flex;flex-direction:column;justify-content:space-between;}
      .vendor{font-size:15pt;font-weight:900;letter-spacing:2pt;color:#111;
              font-family:sans-serif;line-height:1;text-transform:uppercase;}
      .pcode{font-size:6pt;color:#555;font-family:sans-serif;font-weight:700;letter-spacing:1pt;}
      .hr{width:100%;height:0.4pt;background:#ddd;margin:1mm 0;}
      .wt{font-size:6pt;color:#333;font-family:'Courier New',monospace;line-height:1.75;}
      .inum{font-size:7pt;font-weight:700;color:#222;font-family:sans-serif;}
      @media print{body{background:#fff;padding:5mm;}@page{size:auto;margin:5mm;}}
    </style>
    </head><body>${html}
    <script>window.onload=()=>window.print()<\/script>
    </body></html>`);
    w.document.close();
  };

  /* ── Render ── */
  return (
    <div className="rv-dialog-backdrop" onClick={onClose}>
      <div className="rv-print-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="rv-modal-header">
          <div className="rv-modal-icon"><PrintLabelIcon /></div>
          <div>
            <h3 className="rv-modal-title">Print Label — Item #{item.inventoryItemID}</h3>
            <p className="rv-modal-sub">{productName(item.productID)}</p>
          </div>
          <button className="rv-modal-close" onClick={onClose}><CloseIcon /></button>
        </div>

        <div className="rv-modal-body" style={{ gap: 14 }}>

          {/* Shop Code */}
          <div className="rv-field">
            <label className="rv-label">Shop Code</label>
            <input
              className="rv-input"
              type="text"
              maxLength={6}
              value={shopCode}
              onChange={e => setShopCode(e.target.value.toUpperCase())}
              placeholder="e.g. RJ"
              style={{ width: 120 }}
            />
          </div>

          {/* Tag size info */}
          <div className="rv-print-info-bar">
            📏 86 × 40 mm landscape hang tag · Barcode encodes Inventory Item ID #{item.inventoryItemID}
          </div>

          {/* Copies */}
          <div className="rv-field" style={{ flexDirection: "row", alignItems: "center", gap: 12 }}>
            <label className="rv-label" style={{ marginBottom: 0, whiteSpace: "nowrap" }}>Number of Copies</label>
            <input
              className="rv-input"
              type="number" min={1} max={200}
              value={copies}
              onChange={e => setCopies(Math.max(1, Number(e.target.value)))}
              style={{ width: 80 }}
            />
          </div>

          {/* Preview */}
          <div className="rv-field">
            <label className="rv-label">Preview</label>
            <div className="rv-print-preview">
              {fetching ? (
                <div style={{ color: "#b0a080", fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}>
                  <span className="rv-spinner" /> Loading vendor &amp; product data…
                </div>
              ) : (
                <div
                  ref={previewRef}
                  style={{ display: "flex", flexDirection: "column", gap: 14, alignItems: "center", width: "100%" }}
                />
              )}
            </div>
            {!fetching && copies > 3 && (
              <p style={{ fontSize: 11, color: "#b0a090", marginTop: 6, textAlign: "center" }}>
                Preview shows 3 of {copies} copies
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="rv-modal-footer">
          <button className="rv-btn rv-btn--ghost" onClick={onClose}>Cancel</button>
          <button className="rv-btn rv-btn--ghost" onClick={printLabels} disabled={fetching}>
            <PrintIcon /> Print
          </button>
          <button className="rv-btn rv-btn--primary" onClick={downloadPDF} disabled={generating || fetching}>
            {generating ? <span className="rv-spinner" /> : <DownloadIcon />}
            {generating ? "Generating…" : "Download PDF"}
          </button>
        </div>
      </div>

      <style>{`
        .rv-print-modal {
          background: #fff;
          border-radius: var(--rad, 14px);
          max-width: 620px;
          width: 100%;
          box-shadow: 0 20px 60px rgba(51,47,47,.18);
          border: 1px solid var(--border, rgba(185,167,146,.2));
          animation: rv-slide-up .22s cubic-bezier(.34,1.56,.64,1);
          overflow: hidden;
          max-height: 90vh;
          display: flex;
          flex-direction: column;
        }
        .rv-modal-body { overflow-y: auto; flex: 1; }
        .rv-print-info-bar {
          font-size: 12px;
          color: #7a6e68;
          background: rgba(185,167,146,.08);
          border: 1px solid rgba(185,167,146,.2);
          border-radius: var(--rad-sm, 10px);
          padding: 9px 14px;
        }
        .rv-print-preview {
          background: #f5f2ef;
          border: 1.5px solid rgba(185,167,146,.2);
          border-radius: var(--rad-sm, 10px);
          padding: 20px 16px;
          min-height: 180px;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow-x: auto;
        }
        @media (max-width: 600px) {
          .rv-print-modal { max-width: 100%; max-height: 95vh; }
        }
      `}</style>
    </div>
  );
}


/* ══════════════════════════════════════
   TOAST
══════════════════════════════════════ */
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3200);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div className={`rv-toast rv-toast--${type}`}>
      <span className="rv-toast-icon">{type === "success" ? <CheckIcon /> : <AlertIcon />}</span>
      <span className="rv-toast-msg">{message}</span>
      <button className="rv-toast-close" onClick={onClose}><CloseIcon /></button>
    </div>
  );
}

/* ══════════════════════════════════════
   CONFIRM DIALOG
══════════════════════════════════════ */
function ConfirmDialog({ name, onConfirm, onCancel }) {
  return (
    <div className="rv-dialog-backdrop">
      <div className="rv-dialog">
        <div className="rv-dialog-icon"><TrashIcon /></div>
        <h3 className="rv-dialog-title">Delete Inventory Item?</h3>
        <p className="rv-dialog-desc">
          <strong>{name}</strong> will be permanently removed. This action cannot be undone.
        </p>
        <div className="rv-dialog-actions">
          <button className="rv-btn rv-btn--ghost" onClick={onCancel}>Cancel</button>
          <button className="rv-btn rv-btn--danger" onClick={onConfirm}>Delete</button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   INVENTORY ITEM FORM MODAL
══════════════════════════════════════ */
function InventoryItemModal({ initial, onSave, onClose, saving, products, stocks }) {
  const isEdit = !!initial?.inventoryItemID;
  const [form, setForm] = useState({
    stockID:       initial?.stockID       ?? 0,
    productID:     initial?.productID     ?? 0,
    receiptLineID: initial?.receiptLineID ?? 0,
    grossWeight:   initial?.grossWeight   ?? "",
    lessWeight:    initial?.lessWeight    ?? "",
    netWeight:     initial?.netWeight     ?? "",
    quantity:      initial?.quantity      ?? "",
    barcodeID:     initial?.barcodeID     ?? 0,
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const g = parseFloat(form.grossWeight) || 0;
    const l = parseFloat(form.lessWeight)  || 0;
    if (form.grossWeight !== "" || form.lessWeight !== "") {
      setForm(f => ({ ...f, netWeight: parseFloat((g - l).toFixed(3)) }));
    }
  }, [form.grossWeight, form.lessWeight]);

  const validate = () => {
    const e = {};
    if (!form.productID || form.productID === 0) e.productID = "Product is required.";
    if (form.quantity === "" || isNaN(Number(form.quantity)) || Number(form.quantity) < 0)
      e.quantity = "Valid quantity is required.";
    if (form.grossWeight !== "" && isNaN(Number(form.grossWeight))) e.grossWeight = "Must be a valid number.";
    if (form.lessWeight  !== "" && isNaN(Number(form.lessWeight)))  e.lessWeight  = "Must be a valid number.";
    return e;
  };

  const handleSubmit = () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    onSave({ ...initial, ...form });
  };

  const field = (key, value) => {
    setForm(f => ({ ...f, [key]: value }));
    setErrors(v => ({ ...v, [key]: "" }));
  };

  return (
    <div className="rv-dialog-backdrop">
      <div className="rv-dialog rv-dialog--form rv-invitem-modal">
        <div className="rv-modal-header">
          <div className="rv-modal-icon"><InvItemIcon /></div>
          <div>
            <h3 className="rv-modal-title">{isEdit ? "Edit Inventory Item" : "New Inventory Item"}</h3>
            <p className="rv-modal-sub">{isEdit ? "Update the item details below." : "Fill in details to add a new inventory item."}</p>
          </div>
          <button className="rv-modal-close" onClick={onClose}><CloseIcon /></button>
        </div>
        <div className="rv-modal-body">
          <div className="rv-field">
            <label className="rv-label">Product <span className="rv-required">*</span></label>
            <div className="rv-select-wrap">
              <select className={`rv-select ${errors.productID ? "rv-input--err" : ""}`} value={form.productID} onChange={e => field("productID", Number(e.target.value))}>
                <option value={0}>Select product…</option>
                {products.map(p => <option key={p.productID} value={p.productID}>{p.productName}</option>)}
              </select>
              <span className="rv-select-arrow"><ChevronIcon /></span>
            </div>
            {errors.productID && <p className="rv-field-err">{errors.productID}</p>}
          </div>
          <div className="rv-field">
            <label className="rv-label">Linked Stock Entry</label>
            <div className="rv-select-wrap">
              <select className="rv-select" value={form.stockID} onChange={e => field("stockID", Number(e.target.value))}>
                <option value={0}>None / Auto-link</option>
                {stocks.map(s => (
                  <option key={s.stockID} value={s.stockID}>
                    Stock #{s.stockID} — {products.find(p => p.productID === s.productID)?.productName ?? `Product ${s.productID}`} (Qty: {s.quantity})
                  </option>
                ))}
              </select>
              <span className="rv-select-arrow"><ChevronIcon /></span>
            </div>
          </div>
          <div className="rv-field-row-2col">
            <div className="rv-field">
              <label className="rv-label">Gross Weight (g)</label>
              <input className={`rv-input ${errors.grossWeight ? "rv-input--err" : ""}`} type="number" step="0.001" min="0" placeholder="0.000" value={form.grossWeight} onChange={e => field("grossWeight", e.target.value)} />
              {errors.grossWeight && <p className="rv-field-err">{errors.grossWeight}</p>}
            </div>
            <div className="rv-field">
              <label className="rv-label">Less Weight (g)</label>
              <input className={`rv-input ${errors.lessWeight ? "rv-input--err" : ""}`} type="number" step="0.001" min="0" placeholder="0.000" value={form.lessWeight} onChange={e => field("lessWeight", e.target.value)} />
              {errors.lessWeight && <p className="rv-field-err">{errors.lessWeight}</p>}
            </div>
          </div>
          <div className="rv-field-row-2col">
            <div className="rv-field">
              <label className="rv-label">Net Weight (g)</label>
              <input className="rv-input rv-invitem-netweight" type="number" step="0.001" placeholder="Auto-calculated" value={form.netWeight} onChange={e => field("netWeight", e.target.value)} />
              <p className="rv-field-hint">Auto = Gross − Less</p>
            </div>
            <div className="rv-field">
              <label className="rv-label">Quantity <span className="rv-required">*</span></label>
              <input className={`rv-input ${errors.quantity ? "rv-input--err" : ""}`} type="number" min="0" placeholder="0" value={form.quantity} onChange={e => field("quantity", e.target.value)} />
              {errors.quantity && <p className="rv-field-err">{errors.quantity}</p>}
            </div>
          </div>
          <div className="rv-field-row-2col">
            <div className="rv-field">
              <label className="rv-label">Receipt Line ID</label>
              <input className="rv-input" type="number" min="0" placeholder="0" value={form.receiptLineID} onChange={e => field("receiptLineID", Number(e.target.value))} />
            </div>
            <div className="rv-field">
              <label className="rv-label">Barcode ID</label>
              <input className="rv-input" type="number" min="0" placeholder="0" value={form.barcodeID} onChange={e => field("barcodeID", Number(e.target.value))} />
            </div>
          </div>
        </div>
        <div className="rv-modal-footer">
          <button className="rv-btn rv-btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="rv-btn rv-btn--primary" onClick={handleSubmit} disabled={saving}>
            {saving ? <span className="rv-spinner" /> : null}
            {isEdit ? "Update" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   DETAIL DRAWER
══════════════════════════════════════ */
function InventoryItemDrawer({ item, products, stocks, onEdit, onPrint, onClose }) {
  const product = products.find(p => p.productID === item.productID);
  const stock   = stocks.find(s => s.stockID === item.stockID);

  return (
    <div className="rv-dialog-backdrop" onClick={onClose}>
      <div className="rv-invitem-drawer" onClick={e => e.stopPropagation()}>
        <div className="rv-invitem-drawer-header">
          <div className="rv-invitem-drawer-avatar"><InvItemIcon /></div>
          <div className="rv-invitem-drawer-title-wrap">
            <div className="rv-invitem-drawer-name">{product?.productName ?? `Product #${item.productID}`}</div>
            <span className="rv-invitem-id-chip">Item #{item.inventoryItemID}</span>
          </div>
          <button className="rv-modal-close" onClick={onClose}><CloseIcon /></button>
        </div>
        <div className="rv-invitem-drawer-body">
          <div className="rv-invitem-detail-grid">
            <div className="rv-invitem-detail-item">
              <span className="rv-vendor-detail-label"><BoxIcon /> Product</span>
              <span className="rv-vendor-detail-val">{product?.productName ?? "—"}</span>
            </div>
            <div className="rv-invitem-detail-item">
              <span className="rv-vendor-detail-label"><HashIcon /> Stock Entry</span>
              <span className="rv-vendor-detail-val">{item.stockID ? `#${item.stockID}` : "—"}{stock ? ` (Qty: ${stock.quantity})` : ""}</span>
            </div>
            <div className="rv-invitem-detail-item">
              <span className="rv-vendor-detail-label"><WeightIcon /> Gross Weight</span>
              <span className="rv-vendor-detail-val">{item.grossWeight != null && item.grossWeight !== 0 ? `${item.grossWeight} g` : "—"}</span>
            </div>
            <div className="rv-invitem-detail-item">
              <span className="rv-vendor-detail-label"><WeightIcon /> Less Weight</span>
              <span className="rv-vendor-detail-val">{item.lessWeight != null && item.lessWeight !== 0 ? `${item.lessWeight} g` : "—"}</span>
            </div>
            <div className="rv-invitem-detail-item">
              <span className="rv-vendor-detail-label"><WeightIcon /> Net Weight</span>
              <span className="rv-vendor-detail-val rv-invitem-netval">{item.netWeight != null && item.netWeight !== 0 ? `${item.netWeight} g` : "—"}</span>
            </div>
            <div className="rv-invitem-detail-item">
              <span className="rv-vendor-detail-label"><CubeIcon /> Quantity</span>
              <span className="rv-vendor-detail-val rv-invitem-qtyval">{item.quantity ?? "—"}</span>
            </div>
            <div className="rv-invitem-detail-item">
              <span className="rv-vendor-detail-label"><HashIcon /> Receipt Line</span>
              <span className="rv-vendor-detail-val">{item.receiptLineID || "—"}</span>
            </div>
            <div className="rv-invitem-detail-item">
              <span className="rv-vendor-detail-label"><BarcodeIcon /> Barcode ID</span>
              <span className="rv-vendor-detail-val">{item.barcodeID || "—"}</span>
            </div>
            <div className="rv-invitem-detail-item rv-invitem-detail-item--full">
              <span className="rv-vendor-detail-label"><CalIcon /> Last Updated</span>
              <span className="rv-vendor-detail-val">{item.lastUPDTTM ? new Date(item.lastUPDTTM).toLocaleString() : "—"}</span>
            </div>
          </div>
        </div>
        <div className="rv-invitem-drawer-footer">
          <button className="rv-btn rv-btn--ghost" onClick={onClose}>Close</button>
          <button className="rv-btn rv-btn--ghost" onClick={() => { onClose(); onPrint(item); }}>
            <PrintIcon /> Print Label
          </button>
          <button className="rv-btn rv-btn--primary" onClick={() => { onClose(); onEdit(item); }}>
            <EditIcon /> Edit
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   MAIN PAGE
══════════════════════════════════════ */
export default function InventoryItemPage() {
  const [items,    setItems]    = useState([]);
  const [products, setProducts] = useState([]);
  const [stocks,   setStocks]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState("");
  const [filterProduct, setFilterProduct] = useState("all");

  const [modal,   setModal]   = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [drawer,  setDrawer]  = useState(null);
  const [toast,   setToast]   = useState(null);
  const [printItem, setPrintItem] = useState(null);

  const tableRef = useRef(null);
  const [tableReady, setTableReady] = useState(false);
  useResizableColumns(tableRef, tableReady, [50, 200, 110, 90, 90, 90, 100, 120]);

  const fetchAll = async () => {
    try {
      setLoading(true); setTableReady(false);
      const [itemsData, productsData, stocksData] = await Promise.all([
        inventoryItemDtlApi.getAll(),
        productApi.getAll(),
        inventorySummaryApi.getAll(),
      ]);
      setItems(Array.isArray(itemsData) ? itemsData : itemsData?.data ?? []);
      setProducts(Array.isArray(productsData) ? productsData : productsData?.data ?? []);
      setStocks(Array.isArray(stocksData) ? stocksData : stocksData?.data ?? []);
    } catch (err) {
      showToast(err.message || "Failed to load inventory items.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { if (!loading) setTableReady(true); }, [loading]);

  const showToast = (message, type = "success") => setToast({ message, type });

  const handleSave = async (formData) => {
    setSaving(true);
    try {
      if (formData.inventoryItemID) {
        await inventoryItemDtlApi.update(formData);
        showToast("Inventory item updated successfully.");
      } else {
        await inventoryItemDtlApi.create(formData);
        showToast("Inventory item created successfully.");
      }
      setModal(null);
      fetchAll();
    } catch (err) {
      showToast(err.message || "Something went wrong.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm) return;
    try {
      await inventoryItemDtlApi.remove(confirm.id);
      showToast("Inventory item deleted.");
      setConfirm(null);
      fetchAll();
    } catch (err) {
      showToast(err.message || "Delete failed.", "error");
    }
  };

  const productName = (id) =>
    products.find(p => p.productID === id)?.productName ?? `Product #${id}`;

  const usedProductIDs = [...new Set(items.map(i => i.productID))];

  const filtered = items.filter(item => {
    const pName = productName(item.productID).toLowerCase();
    const matchSearch =
      pName.includes(search.toLowerCase()) ||
      String(item.inventoryItemID).includes(search) ||
      String(item.barcodeID).includes(search);
    const matchProduct = filterProduct === "all" || item.productID === Number(filterProduct);
    return matchSearch && matchProduct;
  });

  const { paged, page, totalPages, pageSize, setPage, setPageSize, pageStart, pageEnd, totalItems } =
    usePagination(filtered, 10);

  return (
    <div className="rv-invitem-page">

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {confirm && (
        <ConfirmDialog
          name={`Item #${confirm.id}`}
          onConfirm={handleDelete}
          onCancel={() => setConfirm(null)}
        />
      )}

      {modal && (
        <InventoryItemModal
          initial={modal.data}
          onSave={handleSave}
          onClose={() => setModal(null)}
          saving={saving}
          products={products}
          stocks={stocks}
        />
      )}

      {drawer && (
        <InventoryItemDrawer
          item={drawer}
          products={products}
          stocks={stocks}
          onEdit={(item) => setModal({ data: item })}
          onPrint={(item) => setPrintItem(item)}
          onClose={() => setDrawer(null)}
        />
      )}

      {/* Print Label Modal */}
{printItem && (
  <BarcodeLabelPrinter
    item={printItem}
    product={products.find(p => p.productID === printItem.productID)}
    vendorName="Your Vendor Name"   // pass dynamically if available
    onClose={() => setPrintItem(null)}
  />
)}

      {/* Topbar */}
      <div className="rv-invitem-topbar">
        <div className="rv-invitem-topbar-left">
          <h2 className="rv-invitem-heading">Inventory Items</h2>
          <span className="rv-invitem-count">{items.length} total</span>
        </div>
        <button className="rv-btn rv-btn--primary" onClick={() => setModal({ data: {} })}>
          <PlusIcon /> New Item
        </button>
      </div>

      {/* Filters */}
      <div className="rv-invitem-filters">
        <div className="rv-invitem-search">
          <SearchIcon />
          <input
            placeholder="Search by product, item ID, barcode…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
          {search && (
            <button className="rv-invitem-search-clear" onClick={() => { setSearch(""); setPage(1); }}>
              <CloseIcon />
            </button>
          )}
        </div>
        <div className="rv-invitem-product-filter">
          <div className="rv-select-wrap">
            <select
              className="rv-select"
              value={filterProduct}
              onChange={e => { setFilterProduct(e.target.value); setPage(1); }}
            >
              <option value="all">All Products</option>
              {usedProductIDs.map(id => (
                <option key={id} value={id}>{productName(id)}</option>
              ))}
            </select>
            <span className="rv-select-arrow"><ChevronIcon /></span>
          </div>
        </div>
      </div>

      {/* Table card */}
      <div className="rv-card rv-invitem-card">
        {loading ? (
          <div className="rv-invitem-empty">
            <div className="rv-loader" />
            <p>Loading inventory items…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="rv-invitem-empty">
            <div className="rv-empty-icon"><InvItemIcon /></div>
            <p className="rv-empty-title">No inventory items found</p>
            <p className="rv-empty-sub">{search ? "Try a different search term." : "Add your first inventory item to get started."}</p>
            {!search && (
              <button className="rv-btn rv-btn--primary rv-mt-12" onClick={() => setModal({ data: {} })}>
                <PlusIcon /> New Item
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="rv-table-wrap rv-invitem-table-wrap">
              <table className="rv-table rv-invitem-table" ref={tableRef}>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Product</th>
                    <th>Gross Wt</th>
                    <th>Less Wt</th>
                    <th>Net Wt</th>
                    <th>Qty</th>
                    <th>Barcode ID</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map((item, i) => (
                    <tr
                      key={item.inventoryItemID}
                      className="rv-invitem-row"
                      onClick={() => setDrawer(item)}
                      style={{ cursor: "pointer" }}
                    >
                      <td className="rv-invitem-idx">{pageStart + i}</td>
                      <td>
                        <div className="rv-invitem-product-cell">
                          <span className="rv-invitem-product-name">{productName(item.productID)}</span>
                          {item.stockID ? <span className="rv-invitem-stock-chip">{item.stockID}</span> : null}
                        </div>
                      </td>
                      <td>
                        <span className="rv-invitem-weight">
                          {item.grossWeight != null && item.grossWeight !== 0 ? `${item.grossWeight} g` : <span className="rv-empty-val">—</span>}
                        </span>
                      </td>
                      <td>
                        <span className="rv-invitem-weight">
                          {item.lessWeight != null && item.lessWeight !== 0 ? `${item.lessWeight} g` : <span className="rv-empty-val">—</span>}
                        </span>
                      </td>
                      <td>
                        <span className="rv-invitem-netweight-val">
                          {item.netWeight != null && item.netWeight !== 0 ? `${item.netWeight} g` : <span className="rv-empty-val">—</span>}
                        </span>
                      </td>
                      <td><span className="rv-invitem-qty">{item.quantity ?? 0}</span></td>
                      <td>
                        {item.barcodeID
                          ? <span className="rv-invitem-barcode">{item.barcodeID}</span>
                          : <span className="rv-empty-val">—</span>}
                      </td>
                      <td onClick={e => e.stopPropagation()}>
                        <div className="rv-actions">
                          <button
                            className="rv-action-btn rv-action-btn--print"
                            title="Print Label"
                            onClick={() => setPrintItem(item)}
                          >
                            <PrintIcon />
                          </button>
                          <button
                            className="rv-action-btn rv-action-btn--edit"
                            title="Edit"
                            onClick={() => setModal({ data: item })}
                          >
                            <EditIcon />
                          </button>
                          <button
                            className="rv-action-btn rv-action-btn--del"
                            title="Delete"
                            onClick={() => setConfirm({ id: item.inventoryItemID, name: `Item #${item.inventoryItemID}` })}
                          >
                            <TrashIcon />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={page} totalPages={totalPages} pageSize={pageSize}
              setPage={setPage} setPageSize={setPageSize}
              pageStart={pageStart} pageEnd={pageEnd} totalItems={totalItems}
            />
          </>
        )}
      </div>

      {!loading && filtered.length > 0 && (
        <p className="rv-resize-hint">
          <GripIcon /> Drag column borders to resize · Click a row to view details
        </p>
      )}

      <style>{`
        .rv-action-btn--print:hover {
          background: rgba(185,167,146,.18);
          color: var(--taupe-dark, #8a7060);
        }
      `}</style>
    </div>
  );
}

/* ── Icons ── */
function PlusIcon()         { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>; }
function SearchIcon()       { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>; }
function EditIcon()         { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="14" height="14"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>; }
function TrashIcon()        { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="14" height="14"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>; }
function CloseIcon()        { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>; }
function CheckIcon()        { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>; }
function AlertIcon()        { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>; }
function ChevronIcon()      { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>; }
function GripIcon()         { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" style={{width:13,height:13}}><circle cx="9" cy="5" r="1" fill="currentColor"/><circle cx="9" cy="12" r="1" fill="currentColor"/><circle cx="9" cy="19" r="1" fill="currentColor"/><circle cx="15" cy="5" r="1" fill="currentColor"/><circle cx="15" cy="12" r="1" fill="currentColor"/><circle cx="15" cy="19" r="1" fill="currentColor"/></svg>; }
function InvItemIcon()      { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>; }
function BoxIcon()          { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>; }
function WeightIcon()       { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="12" cy="8" r="4"/><path d="M6 20h12l-2-8H8L6 20z"/></svg>; }
function CubeIcon()         { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 3H8l-2 4h12l-2-4z"/></svg>; }
function HashIcon()         { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><line x1="4" y1="9" x2="20" y2="9"/><line x1="4" y1="15" x2="20" y2="15"/><line x1="10" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="14" y2="21"/></svg>; }
function BarcodeIcon()      { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="1" y="4" width="2" height="16"/><rect x="5" y="4" width="1" height="16"/><rect x="8" y="4" width="2" height="16"/><rect x="12" y="4" width="1" height="16"/><rect x="15" y="4" width="2" height="16"/><rect x="19" y="4" width="1" height="16"/><rect x="21" y="4" width="2" height="16"/></svg>; }
function CalIcon()          { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>; }
function PrintIcon()        { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="14" height="14"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>; }
function DownloadIcon()     { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="14" height="14"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>; }
function PrintLabelIcon()   { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="18" height="18"><rect x="3" y="4" width="18" height="8" rx="2"/><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>; }
function TagIcon()          { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="20" height="20"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>; }
function StickerIcon()      { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="20" height="20"><rect x="3" y="3" width="18" height="18" rx="3"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg>; }